/**
 * Route vocabulary for the storefront Mini App.
 *
 * A tiny, dependency-free hash router (see {@link ./router}) drives navigation —
 * Telegram WebApps run inside an embedded webview where the History API is awkward,
 * so a hash strategy is the most robust default. Routes are expressed as plain
 * builders to keep call sites typed and free of stringly-typed paths.
 */
/** Tabs of the Mini-RPG "Развитие" screen (inventory / weapons / stats / alchemy / shop). */
export type RpgDevelopTab =
  | "inventory"
  | "weapons"
  | "upgrade"
  | "alchemy"
  | "shop";

/** Whitelist of valid develop tabs (route validation). */
export const RPG_DEVELOP_TABS: readonly RpgDevelopTab[] = [
  "inventory",
  "weapons",
  "upgrade",
  "alchemy",
  "shop",
];

export const routes = {
  home: (): string => "/",
  shop: (): string => "/shop",
  /** Shop scoped to a game; optional category narrows the listing. */
  shopGame: (gameId: number, categoryId?: number): string =>
    categoryId === undefined
      ? `/shop/${gameId}`
      : `/shop/${gameId}/${categoryId}`,
  product: (productId: number): string => `/product/${productId}`,
  /** Order-confirmation screen for the current cart (ТЗ §5). */
  checkout: (): string => "/checkout",
  orders: (): string => "/orders",
  /** A single order's screen (detail + realtime chat). */
  order: (orderId: number): string => `/orders/${orderId}`,
  /** Referral program: invite link, star-glitter balance and stats. */
  referrals: (): string => "/referrals",
  /** Mini-RPG "earn mora" screen (battles + currency exchange). */
  earn: (): string => "/earn",
  /** Mini-RPG hub (профиль + разделы), ТЗ №5. */
  rpg: (): string => "/rpg",
  /** Mini-RPG: бой с мобами. */
  rpgBattle: (): string => "/rpg/battle",
  /** Mini-RPG: развитие (инвентарь/оружие/прокачка/алхимия/магазин); optional tab. */
  rpgDevelop: (tab?: RpgDevelopTab): string =>
    tab === undefined ? "/rpg/develop" : `/rpg/develop/${tab}`,
  /** Mini-RPG: рейтинги. */
  rpgRatings: (): string => "/rpg/ratings",
  /** Mini-RPG: обмен моры на примогемы. */
  rpgExchange: (): string => "/rpg/exchange",
  /** Mini-RPG: переводы другим игрокам. */
  rpgTransfers: (): string => "/rpg/transfers",
  /** Mini-RPG: FAQ. */
  rpgFaq: (): string => "/rpg/faq",
  support: (): string => "/support",
  /** A single support ticket's screen (detail + message thread, ТЗ §8). */
  supportTicket: (ticketId: number): string => `/support/${ticketId}`,
  /** App preferences (theme switch). */
  settings: (): string => "/settings",
} as const;

/** Discriminated match of the current location against known routes. */
export type RouteMatch =
  | { readonly name: "home" }
  | { readonly name: "shop"; readonly gameId?: number; readonly categoryId?: number }
  | { readonly name: "product"; readonly productId: number }
  | { readonly name: "checkout" }
  | { readonly name: "orders" }
  | { readonly name: "order"; readonly orderId: number }
  | { readonly name: "referrals" }
  | { readonly name: "earn" }
  | { readonly name: "rpg" }
  | { readonly name: "rpg-battle" }
  | { readonly name: "rpg-develop"; readonly tab?: RpgDevelopTab }
  | { readonly name: "rpg-ratings" }
  | { readonly name: "rpg-exchange" }
  | { readonly name: "rpg-transfers" }
  | { readonly name: "rpg-faq" }
  | { readonly name: "support" }
  | { readonly name: "support-ticket"; readonly ticketId: number }
  | { readonly name: "settings" }
  | { readonly name: "not-found" };

function toPositiveInt(raw: string | undefined): number | undefined {
  if (raw === undefined) {
    return undefined;
  }
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : undefined;
}

/**
 * Resolve a path (e.g. ``/shop/3/12``) into a typed {@link RouteMatch}.
 *
 * Unknown or malformed paths resolve to ``not-found`` so callers always handle a
 * total set of cases.
 */
export function matchRoute(path: string): RouteMatch {
  const clean = path.split("?")[0]?.replace(/\/+$/, "") ?? "";
  const segments = clean.split("/").filter((s) => s.length > 0);

  if (segments.length === 0) {
    return { name: "home" };
  }

  const [head, a, b] = segments;

  if (head === "shop") {
    const gameId = toPositiveInt(a);
    const categoryId = toPositiveInt(b);
    if (a !== undefined && gameId === undefined) {
      return { name: "not-found" };
    }
    if (b !== undefined && categoryId === undefined) {
      return { name: "not-found" };
    }
    return {
      name: "shop",
      ...(gameId !== undefined ? { gameId } : {}),
      ...(categoryId !== undefined ? { categoryId } : {}),
    };
  }

  if (head === "product") {
    const productId = toPositiveInt(a);
    if (productId !== undefined && b === undefined) {
      return { name: "product", productId };
    }
    return { name: "not-found" };
  }

  if (head === "checkout" && a === undefined) {
    return { name: "checkout" };
  }

  if (head === "referrals" && a === undefined) {
    return { name: "referrals" };
  }

  if (head === "earn" && a === undefined) {
    return { name: "earn" };
  }

  if (head === "rpg") {
    if (a === undefined) {
      return { name: "rpg" };
    }
    if (a === "battle" && b === undefined) {
      return { name: "rpg-battle" };
    }
    if (a === "develop") {
      if (b === undefined) {
        return { name: "rpg-develop" };
      }
      if ((RPG_DEVELOP_TABS as readonly string[]).includes(b)) {
        return { name: "rpg-develop", tab: b as RpgDevelopTab };
      }
      return { name: "not-found" };
    }
    if (a === "ratings" && b === undefined) {
      return { name: "rpg-ratings" };
    }
    if (a === "exchange" && b === undefined) {
      return { name: "rpg-exchange" };
    }
    if (a === "transfers" && b === undefined) {
      return { name: "rpg-transfers" };
    }
    if (a === "faq" && b === undefined) {
      return { name: "rpg-faq" };
    }
    return { name: "not-found" };
  }

  if (head === "support") {
    if (a === undefined) {
      return { name: "support" };
    }
    const ticketId = toPositiveInt(a);
    if (ticketId !== undefined && b === undefined) {
      return { name: "support-ticket", ticketId };
    }
    return { name: "not-found" };
  }

  if (head === "settings" && a === undefined) {
    return { name: "settings" };
  }

  if (head === "orders") {
    if (a === undefined) {
      return { name: "orders" };
    }
    const orderId = toPositiveInt(a);
    if (orderId !== undefined && b === undefined) {
      return { name: "order", orderId };
    }
    return { name: "not-found" };
  }

  return { name: "not-found" };
}
